import { AIFilterPrompt } from '@statsbomb/kitbag-ai-assistant';
import { useFilterScope } from '@statsbomb/kitbag-global-football';
import { getAiFilterJsonSchema, getMetricFilterMapping } from '@statsbomb/parachute-types';

import { useAtomValue, useSetAtom } from 'jotai';
import { ReactElement, Suspense, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { aiFilterContextInfosAtom } from '@/atoms/ai/aiFilterContext';
import { filtersAtom } from '@/atoms/filters/filter';
import { metricFilterMappingEnabledAtom, mappedMetricFilterAtom } from '@/atoms/metric/metricFilterMapping';
import { playerMetricsAtom } from '@/atoms/metric/playerMetrics';
import env from '@/env';
import { useAiFilterPromptFeatureToggle } from '@/hooks/featureFlags/useAiFilterPromptFeatureToggle';
import { replaceMappedFilters } from '@/hooks/useApplyMetricFilterMapping/useApplyMetricFilterMapping';
import { useMetricPitchPresetNavigation } from '@/hooks/useMetricPitchPresetNavigation/useMetricPitchPresetNavigation';
import { snakeToCamel } from '@/utils/queries';
import { applyFilterToolCallInput, withMetricKeyField, withoutMappingOwnedFields } from './applyFilterToolCall';
import type { AgentStreamError, ToolCall } from '@statsbomb/kitbag-ai-assistant';
import type { FilterScopeOptions } from '@statsbomb/kitbag-global-football';

const APPLY_FILTER_TOOL_NAME = 'apply_filter';

const applyMetricKey = (
  metricKey: string,
  input: Record<string, unknown>,
  currentMapping: ReturnType<typeof getMetricFilterMapping>,
  setFilters: ReturnType<typeof useSetAtom<typeof filtersAtom>>,
  selectMetricPreset: (key: string) => void,
): void => {
  const callMapping = getMetricFilterMapping(metricKey);
  if (callMapping === currentMapping) {
    // already selected — strip mapping-owned fields and re-apply the mapping in one update
    setFilters(prev =>
      replaceMappedFilters(applyFilterToolCallInput(withoutMappingOwnedFields(input), prev), callMapping, callMapping),
    );
  } else {
    // strip mapping-owned fields then navigate; useApplyMetricFilterMapping will apply the mapping
    setFilters(prev => applyFilterToolCallInput(withoutMappingOwnedFields(input), prev));
    selectMetricPreset(metricKey);
  }
};

const AIFilterPromptPanelInner = ({ agentEndpoint }: { agentEndpoint: string }): ReactElement => {
  const { t } = useTranslation(['filters', 'metrics']);
  const setFilters = useSetAtom(filtersAtom);
  const { competitionInfos, seasonInfos, availableMatchPeriods, squadPlayerInfos } =
    useAtomValue(aiFilterContextInfosAtom);
  const { setOptions } = useFilterScope();

  const isMappingModeOn = useAtomValue(metricFilterMappingEnabledAtom);
  const allPlayerMetrics = useAtomValue(playerMetricsAtom);
  const currentMapping = useAtomValue(mappedMetricFilterAtom);
  const selectMetricPreset = useMetricPitchPresetNavigation();

  const selectableMetrics = useMemo(() => {
    if (!isMappingModeOn) return [];
    return allPlayerMetrics
      .filter(metric => getMetricFilterMapping(metric.key) !== undefined)
      .map(metric => ({ key: metric.key, label: t(`${snakeToCamel(metric.key)}.name`, { ns: 'metrics' }) }));
  }, [isMappingModeOn, allPlayerMetrics, t]);

  const toolSchema = useMemo(() => withMetricKeyField(getAiFilterJsonSchema(), selectableMetrics), [selectableMetrics]);

  useEffect(() => {
    setOptions({ availableMatchPeriods: availableMatchPeriods as FilterScopeOptions['availableMatchPeriods'] });
  }, [availableMatchPeriods]);

  const handleToolCall = useCallback(
    (call: ToolCall) => {
      const { name, input } = call;
      if (name !== APPLY_FILTER_TOOL_NAME || !input) return;
      const { metricKey } = input;
      if (typeof metricKey === 'string' && selectableMetrics.some(metric => metric.key === metricKey)) {
        applyMetricKey(metricKey, input, currentMapping, setFilters, selectMetricPreset);
        return;
      }
      setFilters(prev => applyFilterToolCallInput(input, prev));
    },
    [setFilters, selectableMetrics, currentMapping, selectMetricPreset],
  );

  const handleError = useCallback((err: AgentStreamError) => {
    // eslint-disable-next-line no-console
    console.error('AI filter prompt error', err);
  }, []);

  const buildPayload = useCallback(
    (prompt: string) => ({
      inputText: prompt,
      ...(competitionInfos.length > 0 && { competitions: competitionInfos }),
      ...(seasonInfos.length > 0 && { seasons: seasonInfos }),
      ...(availableMatchPeriods.length > 0 && { availableMatchPeriods }),
      ...(squadPlayerInfos.length > 0 && { squadPlayers: squadPlayerInfos }),
    }),
    [availableMatchPeriods, competitionInfos, seasonInfos, squadPlayerInfos],
  );

  return (
    <AIFilterPrompt
      agentEndpoint={agentEndpoint}
      toolSchema={toolSchema}
      onToolCall={handleToolCall}
      onError={handleError}
      buildPayload={buildPayload}
      label={t('aiPrompt.label')}
      placeholder={t('aiPrompt.placeholder')}
      submitLabel={t('aiPrompt.submitLabel')}
      badgeText={t('aiPrompt.badgeText')}
      helpText={t('aiPrompt.helpText')}
    />
  );
};

export const AIFilterPromptPanel = (): ReactElement | null => {
  const enabled = useAiFilterPromptFeatureToggle();
  const agentEndpoint = env.VITE_AI_FILTER_PROMPT_AGENT_ENDPOINT;
  if (!enabled || !agentEndpoint) return null;
  return (
    <Suspense fallback={null}>
      <AIFilterPromptPanelInner agentEndpoint={agentEndpoint} />
    </Suspense>
  );
};
